import { COMMERCE } from '@/config/site';
import { cn } from '@/lib/cn';
import { discountRatio, formatCents, formatCentsPerKg } from '@/lib/money';
import { formatPercent } from '@/lib/format';
import type { PricedVariant } from '@/types/catalog';

import { Badge } from '../ui/badge';

interface PriceProps {
  variant: PricedVariant;
  /** Prefixo usado nos cards, onde o preço mostrado é o do menor tamanho. */
  prefix?: string;
  size?: 'sm' | 'md' | 'lg';
  showPerKg?: boolean;
  className?: string;
}

const SIZES = {
  sm: { price: 'text-lg', compare: 'text-xs' },
  md: { price: 'text-2xl', compare: 'text-sm' },
  lg: { price: 'text-4xl', compare: 'text-base' },
} as const;

/**
 * Único lugar onde preço é renderizado. Respeita `COMMERCE.showPrices`, então
 * a loja inteira troca para o modo "consulte o valor" com uma flag.
 */
export function Price({ variant, prefix, size = 'md', showPerKg = true, className }: PriceProps) {
  const scale = SIZES[size];

  if (!COMMERCE.showPrices) {
    return (
      <p className={cn('font-display font-bold text-brown-700', scale.price, className)}>
        Consulte o valor
      </p>
    );
  }

  const discount = discountRatio(variant.priceCents, variant.compareAtPriceCents);

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {variant.compareAtPriceCents !== undefined && discount !== null && (
        <div className="flex items-center gap-2">
          <span className={cn('text-brown-700/70 line-through', scale.compare)}>
            {formatCents(variant.compareAtPriceCents)}
          </span>
          <Badge variant="primary" size="sm">
            -{formatPercent(discount)}
          </Badge>
        </div>
      )}

      {/* O prefixo fica na própria linha: em linha, quebra em três no grid
          de duas colunas do mobile. */}
      {prefix && (
        <span className="text-[11px] leading-none font-medium text-brown-700">{prefix}</span>
      )}

      <p className={cn('font-display leading-none font-extrabold text-brown-900', scale.price)}>
        {formatCents(variant.priceCents)}
      </p>

      {showPerKg && variant.weightKg >= 1 && (
        <p className="text-xs font-medium text-brown-700/80">
          {formatCentsPerKg(variant.priceCents, variant.weightKg)}
        </p>
      )}
    </div>
  );
}
